/* eslint-disable camelcase */
import _ from "lodash"
import { observer } from "mobx-react-lite"
import moment from "moment"
import { Modal, Skeleton, Spinner, View } from "native-base"
import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  ActivityIndicator,
  Appearance,
  Dimensions,
  FlatList,
  ImageStyle,
  ListRenderItem,
  Platform,
  TextInput,
  TextStyle,
  ViewStyle,
} from "react-native"
import {
  AutoImage,
  Button,
  FormControl,
  Input,
  JalaIcon,
  JalaLoading,
  LazyRender,
  ModelPicker,
  OfflineMode,
  OfflineModeIndicator,
  ProgressDialog,
  Screen,
  Text,
  TooltipContent,
} from "../../../../components"
import { Farm } from "../../../../database"
import { translate, TxKeyPath } from "../../../../i18n"
import { useStores } from "../../../../models"
import { goBack, navigate } from "../../../../navigators"
import { color, nativeBaseTheme } from "../../../../theme"
import { CompositeScreenProps, useFocusEffect, useIsFocused } from "@react-navigation/native"
import numeral from "numeral"
import { TouchableOpacity } from "react-native-gesture-handler"
import { SafeAreaView } from "react-native-safe-area-context"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { PondStore, PondStoreModel } from "../../../../models/app-store/farm-store/pond-store/pond-store"
import { delay } from "../../../../utils/delay"
import Tooltip from "react-native-walkthrough-tooltip"
import { CycleStoreModel } from "../../../../models/app-store/farm-store/pond-store/cycle-store/cycle-store"
import { isAlive } from "mobx-state-tree"

const windowWidth = Dimensions.get('window').width;

const emptyPond = require("./empty-pond.png")

export const weathers = [
  { id: 400, name: 'thunderstorm', iconName: 'weather-rain-storm' },
  { id: 450, name: 'rain', iconName: 'weather-rain-heavy' },
  { id: 500, name: 'drizzle', iconName: 'weather-rain-normal' },
  { id: 600, name: 'cloudy', iconName: 'weather-cloudy' },
  { id: 700, name: 'sunnyCloudy', iconName: 'weather-sunny-cloudy' },
  { id: 800, name: 'sunny', iconName: 'weather-sunny' },
  { id: 900, name: 'scorching', iconName: 'weather-sunny-heavy' }
];

export const waterColor = [
  { color: '#FEFF04', value: 16711428, name: 'yellow' },
  { color: '#DCF929', value: 14481705, name: 'yellowGreen' },
  { color: '#C07449', value: 12612681, name: 'brown' },
  { color: '#877626', value: 8877606, name: 'brownGreen' },
  { color: '#694917', value: 6899991, name: 'darkBrown' },
  { color: '#B8864C', value: 12093004, name: 'lightBrown' },
  { color: '#923B20', value: 9583392, name: 'brownRed' },
  { color: '#54EB37', value: 5565239, name: 'green' },
  { color: '#79881B', value: 7964699, name: 'greenBrown' },
  { color: '#32A683', value: 3319427, name: 'greenBlue' },
  { color: '#285B16', value: 2644758, name: 'darkGreenBrown' },
  { color: '#259912', value: 2464018, name: 'darkGreen' },
  { color: '#1C1D1C', value: 1842460, name: 'black' }
];

const ROOT: ViewStyle = {
  flex: 1,
  // flexGrow: 1,
}

const TOP_BAR_CONTAINER: ViewStyle = {
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "row"
}

const TOP_BAR_SEARCH_BUTTON: ViewStyle = {
  marginRight: 16,
}

const TOP_BAR_CONFIG_BUTTON: ViewStyle = {
  marginRight: 16,
}

const TOP_BAR_RIGHT_BUTTON: ViewStyle = {
  paddingHorizontal: 16,
  paddingLeft: 0
}

const IMG_CONTAINER: TextStyle = {
  flex: 1,
  marginBottom: 56,
  alignItems: "center",
  justifyContent: "center",
}

const EMPTY_POND_TEXT: TextStyle = {
  marginTop: 8
}

const BUTTON_WRAPPER = (background): ViewStyle => ({
 position: 'relative',
 width: windowWidth,
 paddingTop: 24,
 backgroundColor: background ? color.palette.white : undefined
})

const BUTTON: TextStyle = {
  // width: windowWidth - 32,
  marginHorizontal: 16,
  marginBottom: 16,
}

const FLAT_LIST_CONTAINER: ViewStyle = {
  paddingTop: 10,
  paddingBottom: 20
}

const POND_ITEM_CONTAINER: ViewStyle = {
  position: 'relative',
  backgroundColor: color.palette.white,
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 8,
  marginHorizontal: 16,
  marginTop: 8,
  flex: 1
}

const POND_ITEM = (isEmpty: boolean): ViewStyle => ({
  height: 60 + (isEmpty ? 0 : 24),
  paddingVertical: 8,
  paddingBottom: 8.5,
  borderBottomWidth: isEmpty ? 0 : 1,
  borderBottomColor: color.palette.gray200
})

const POND_HEADER = (top: boolean): ViewStyle => ({
  flexDirection: 'row',
  justifyContent: top ? 'space-between': 'flex-start',
  // alignItems: 'center',
  paddingHorizontal: 12,
  marginTop: top ? 0 : 6
})


const SUB_HEADER_POND: ViewStyle ={
  flexDirection: 'row',
  alignItems: 'center'
}

const SEARCH_HEADER: ViewStyle = {
  ...SUB_HEADER_POND,
  flex: 1,
}

const ICON_PRO: ViewStyle = {
  width: 16,
  height: 16,
  borderRadius: 16/2,
  backgroundColor: color.palette.orange500,
  alignItems: 'center',
}

const POND_ITEM_CONTENT: ViewStyle = {
  paddingHorizontal: 12
}

const POND_ITEM_SKELETON: ViewStyle = {
  paddingVertical: 12
}

const POND_UPDATE_TEXT: TextStyle = {
  marginTop: 8.5,
  marginBottom: 12
}

const MEASUREMENT_WRAPPER: ViewStyle = {
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: 16
}

const MEASUREMENT_ITEM: ViewStyle = {
  flex: 1,
  height: 72
}

const MEASUREMENT_CONTENT: ViewStyle = {
  height: 21,
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: 4,
}

const MEASUREMENT_TEXT: TextStyle = {
  color: color.palette.blue600,
  marginLeft: 8
}

const EMPTY_POND: ViewStyle = {
  alignItems: 'stretch',
  borderRadius: 4
}

const EMPTY_POND_IMAGE: ImageStyle = {
  width: 76,
  height: 68,
  alignSelf: 'flex-end',
}

// const EMPTY_POND_IMAGE = {
//   width: 78,
//   height: 68,
//   alignSelf: 'flex-end'
// } as any

const BUTTON_CYCLE: TextStyle = {
  marginVertical: 8,
  alignSelf: 'stretch'
}

const WATER_ICON = (color: string): ViewStyle => ({
  width: 13.5,
  height: 13.5,
  borderRadius: 4,
  backgroundColor: color
})

const CLOSED_CYCLE: ViewStyle = {
  backgroundColor: color.palette.white,
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 8,
  marginHorizontal: 16,
  marginTop: 8
}

const CLOSED_CYCLE_CONTENT: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center'
}

const CLOSED_CYCLE_CONTENT_ITEM: ViewStyle = {
  height: 48,
  paddingHorizontal: 12,
  flex: 1
}

const FLEX: ViewStyle = {
  flex: 1,
  marginHorizontal: 6
}

const CLOSED_CYCLE_BTN_WRAPPER: ViewStyle = {
  ...FLEX,
  height: 40,
  marginTop: 10,
  marginBottom: 16,
  flexDirection: 'row',
  justifyContent: 'space-between'
}

const TEXT_FIELD: ViewStyle = {
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  marginVertical: 0,
  paddingVertical: 0,
  backgroundColor: 'green',
  minHeight: 35
}

const BTN_CANCEL: ViewStyle = {
  marginHorizontal: 16
}

const HEADER_CONTAINER = (height): ViewStyle => ({
  justifyContent: "space-between",
  flexDirection: "row",
  alignItems: 'center',
  backgroundColor: color.primary,
  height: height
})

const HEADER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center'
}

const BACK_BUTTON: ViewStyle = {
  marginLeft: 16,
  marginRight: 12
}

const NUMBER_WRAPPER = (pond): ViewStyle => ({
  backgroundColor: color.primary,
  width: 24,
  height: 24,
  borderRadius: 12,
  position: 'absolute',
  top: pond ? -10 : -12,
  right: pond ? 3 : undefined,
  left: pond ? undefined : -10,
  borderWidth: 1,
  borderColor: color.palette.white,
  alignItems: 'center'
})

const POND_TOOLTIP_CONTENT_STYLE: ViewStyle = {
  height: 90
}

const CONTAINER_ITEM: ViewStyle = {
  flex:1,
  width: '100%'
}

const PondItemHeader = (props) => {
  const { line, name, pro, colorScheme, onPressName, onPress, cycle, data } = props;
  const doc = (cycle || {}).age || "-";
  const date = cycle && (moment(cycle.started_at).add(cycle.age - (cycle.initial_age || 0), 'days').format('DD MMM YYYY'));
  const rfid = data.rfid || '-';

  return (
    <View style={POND_ITEM(line)}>
      <View style={POND_HEADER(true)}>
        <View flex={1}>
          <TouchableOpacity onPress={onPressName} disabled={!cycle}>
            <View style={SUB_HEADER_POND}>
              <Text preset="title3" text={name} colorScheme={colorScheme} numberOfLines={1} isTruncated mr="6px" />
              {pro && (
                <View style={ICON_PRO}>
                  <JalaIcon name="premium" size={14} color={color.palette.white} />
                </View>
              )}
            </View>
            <Text preset="body3" text={`RFID: ${rfid}`} my="2px" colorScheme={colorScheme} color={color.palette.blue700} />
            {!line && (
              <Text colorScheme={colorScheme}>
                <Text preset="body3" text={`DoC ${doc}`} mr="6px" colorScheme={colorScheme} />
                {' · '}
                <Text preset="body3" text={date} ml="6px" colorScheme={colorScheme} />
              </Text>
            )}
          </TouchableOpacity>
        </View>
        <View marginLeft="32px" paddingY="2px">
          <Button testID="jala:id/setting-button" preset="link" colorScheme={colorScheme} onPress={onPress}>
            <JalaIcon name="setting" size={18} color={color.palette.gray700} />
          </Button>
        </View>
      </View>
    </View>
  )
}


const ItemIcon = (props) => {
  const { weather, water } = props;
  const empty = !weather && !water;
  return (
    <View style={MEASUREMENT_TEXT}>
      {weather && <JalaIcon name={weather.iconName} size={20} color={color.palette.blueAzure} />}
      {water && <View style={WATER_ICON(water.color)} />}
      {empty && <Text preset="body2" text="-" />}
    </View>
  )
}

const MeasurementItemTime = (props) => {
  const { label, day, sameDayMultiMeasurements = [], colorScheme } = props;
  const isMorning = day === 'morning';
  const isIcon = ['weather', 'color'].includes(label);
  let multiMeasurement = sameDayMultiMeasurements.filter(m => (m.temperature || m.do || m.ph || m.salinity)).find(
    (sameDayMultiMeasurement: any) => (
      (isMorning && sameDayMultiMeasurement.measured_time >= '00:00:00' && sameDayMultiMeasurement.measured_time < '12:00:00')
      || (!isMorning && sameDayMultiMeasurement.measured_time >= '12:00:00' && sameDayMultiMeasurement.measured_time < '24:00:00')
    )
  );
  if (multiMeasurement && (!multiMeasurement.weather || !multiMeasurement.color)) {
    const multiMeasurementWeatherColor = sameDayMultiMeasurements.filter(m => (m.weather || m.color)).find(
      (sameDayMultiMeasurement: any) => (
        (isMorning && sameDayMultiMeasurement.measured_time >= '00:00:00' && sameDayMultiMeasurement.measured_time < '12:00:00')
        || (!isMorning && sameDayMultiMeasurement.measured_time >= '12:00:00' && sameDayMultiMeasurement.measured_time < '24:00:00')
      )
    );
    if (multiMeasurementWeatherColor) {
      multiMeasurement = {
        ...multiMeasurement,
        weather: multiMeasurement.weather || multiMeasurementWeatherColor.weather,
        weather_anomaly: multiMeasurement.weather_anomaly || multiMeasurementWeatherColor.weather_anomaly,
        color: multiMeasurement.color || multiMeasurementWeatherColor.color,
        color_anomaly: multiMeasurement.color_anomaly || multiMeasurementWeatherColor.color_anomaly
      }
    }
  }

  const anomaly = multiMeasurement ? multiMeasurement[`${label}_anomaly`] : false;
  const val = multiMeasurement ? multiMeasurement[label] : null;
  const value = (val && numeral(val).format('0,0.[00]')) || "-";
  const waterIcon = label === 'color' ?  waterColor.find(item => item.value === val) : null;
  const weatherIcon = label === 'weather' ? weathers.find(item => item.id === val) : null;
  const textColor = (anomaly) => anomaly
    ? `danger.${colorScheme === 'light' ? '600' : '400'}`
    : `primary.${colorScheme === 'light' ? '700' : '300'}`

  return (
    <View style={MEASUREMENT_CONTENT}>
      <View width="18px">
        <JalaIcon name={day} size={15} color={color.palette.gray600} />
      </View>
      {isIcon && val ? <View height="20px" justifyContent="center">
        <ItemIcon weather={weatherIcon} water={waterIcon} />
      </View> : <Text preset="body2" text={value} style={MEASUREMENT_TEXT} color={textColor(anomaly)} />}
    </View>
  )
}

const renderMeasurementItemPlaceholder = () => {
  return <View height="54px">
    <Skeleton.Text lines={2} marginTop="10px" paddingRight="24px"></Skeleton.Text>
  </View>
}

const MeasurementItem = (props) => {
  const { sameDayMultiMeasurements, loading, label, colorScheme } = props;
  // loading={!cycleStore || !cycleStore.syncedWaterQualityMultiMeasurement}
  return (
    <View style={MEASUREMENT_ITEM}>
      <Text preset="title4" tx={`pondScreen.${label}` as TxKeyPath} color={color.palette.gray800} colorScheme={colorScheme} />
      <LazyRender loading={loading} renderPlaceholder={renderMeasurementItemPlaceholder}>
        <MeasurementItemTime label={label} day="morning" sameDayMultiMeasurements={sameDayMultiMeasurements} colorScheme={colorScheme} />
        <MeasurementItemTime label={label} day="night" sameDayMultiMeasurements={sameDayMultiMeasurements} colorScheme={colorScheme} />
      </LazyRender>
    </View>
  )
}

const ItemContent = observer((props: any) => {
  const { data : { last_cycle : { multi_measurements = [], cycleStore }}, colorScheme } = props;
  const multiMeasurement: any = _.first(multi_measurements) || {};
  const loading = !((isAlive(cycleStore) && cycleStore) || {}).syncedWaterQualityMultiMeasurement
  const sameDayMultiMeasurements = multi_measurements.filter((mm: any) => (
    mm.measured_date === multiMeasurement.measured_date
  ));

  return (
    <>
      <View style={MEASUREMENT_WRAPPER}>
        <MeasurementItem colorScheme={colorScheme} label="temperature" loading={loading} sameDayMultiMeasurements={sameDayMultiMeasurements} />
        <MeasurementItem colorScheme={colorScheme} label="do" loading={loading} sameDayMultiMeasurements={sameDayMultiMeasurements} />
        <MeasurementItem colorScheme={colorScheme} label="ph" loading={loading} sameDayMultiMeasurements={sameDayMultiMeasurements} />
      </View>
      <View style={MEASUREMENT_WRAPPER}>
        <MeasurementItem colorScheme={colorScheme} label="salinity" loading={loading} sameDayMultiMeasurements={sameDayMultiMeasurements} />
        <MeasurementItem colorScheme={colorScheme} label="weather" loading={loading} sameDayMultiMeasurements={sameDayMultiMeasurements} />
        <MeasurementItem colorScheme={colorScheme} label="color" loading={loading} sameDayMultiMeasurements={sameDayMultiMeasurements} />
      </View>
    </>
  )
})


const EmptyContent = (props) => {
  const { onPress, colorScheme = Appearance.getColorScheme() } = props;
  const img = require("./shrimp-alive.png");
  return (
    <View style={EMPTY_POND}>
      <View
        height="76px"
        backgroundColor={color.background}
        borderRadius="8px"
        marginBottom="8px"
        flexDirection="row"
        justifyContent="space-between"
        overflow="hidden"
      >
        <View flex={1}>
          <Text
            tx="farmPondListScreen.noRecord"
            preset="display3"
            color={color.palette.gray700}
            numberOfLines={2}
            marginY="10px" marginLeft="12px" marginRight="32px"
          />
        </View>
        <AutoImage source={img} style={EMPTY_POND_IMAGE} />
      </View>
      <Button
        testID="jala:id/start-cycle-button"
        tx="pondScreen.startCycle"
        style={BUTTON_CYCLE}
        colorScheme={colorScheme}
        onPress={onPress}
      />
    </View>
  )
}

export const FarmPondHeader: FC<CompositeScreenProps<any, any>> = observer(function FarmPondHeader(props){
  const { navigation, route: { params: { data } } } = props;
  const {
    // appStore,
    appStore: {
      farmStore,
      // offlineData,
      // errorOffline: errors
    }
  } = useStores()
  const { farm } = farmStore || FarmStoreModel.create()

  const isOwner = useMemo(() => (
    farm?.farm_user_role_id === 3
  ), [farm?.farm_user_role_id])

  const backIcon = Platform.OS === "ios" ? 'arrow_back_ios' : 'arrow_back';
  const colorScheme = "light"
  const inputRef = useRef<TextInput>()

  const headerHeight = 55.8;
  const [ hidden, setHide ] = useState<boolean>(true);
  const handleChangeText = (text) => navigation.setParams({ data: { ...data, text } });
  const handleCancel = () => {
    setHide(!hidden);
    navigation.setParams({data: { ...data, text: "" }})
  }

  return (<>
    <View backgroundColor={color.primary}>
      <SafeAreaView edges={["top"]} />
      <View style={HEADER_CONTAINER(headerHeight)}>
        {hidden ? (
          <>
            <View style={HEADER}>
              <Button testID="jala:id/back-button" preset="link" textPreset="display1" style={BACK_BUTTON}
                      colorScheme={colorScheme} onPress={navigation.goBack}>
                <JalaIcon name={backIcon} size={25} color={color.palette.white} />
              </Button>
              <Text tx="farmPondListScreen.title" preset="title2" color={color.palette.white} />
            </View>
            <View style={TOP_BAR_CONTAINER}>
              <Button testID="jala:id/search-button" preset="link" colorScheme="light" style={TOP_BAR_SEARCH_BUTTON}
                      onPress={() => setHide(!hidden)}>
                <JalaIcon name="search" size={24} color={color.palette.white} />
              </Button>
              {/* {(offlineData || 0) !== 0 && ( */}
              {/*   <Button preset="link" colorScheme="light" style={TOP_BAR_SEARCH_BUTTON} onPress={handleUpload}> */}
              {/*     <JalaIcon name="cloud-upload" size={25} color={color.palette.white} /> */}
              {/*   </Button> */}
              {/* )} */}
              {isOwner &&
                <Button testID="jala:id/setting-button" preset="link" colorScheme="light" style={TOP_BAR_CONFIG_BUTTON}
                        onPress={() => navigate("Home.Stack", "Home.Farm.Edit", { ...farm })}>
                  <JalaIcon name="setting" size={24} color={color.palette.white} />
                </Button>}
            </View>
          </>
        ) : (
          <View style={SEARCH_HEADER}>
            <Input ref={inputRef} style={TEXT_FIELD} placeholderTx="searchPondPlaceholder" borderRadius="40px" px="12px"
                  ml="16px" backgroundColor={color.palette.white} onChangeText={handleChangeText} />
            <Button testID="jala:id/cancel-button" preset="link" textPreset="title3" textColor={color.palette.white}
                    style={BTN_CANCEL} onPress={handleCancel} tx="cancel" colorScheme="light" />
          </View>
        )}
      </View>
    </View>
  </>)
})

export const FarmPondListSearch: FC<CompositeScreenProps <any,any>> = observer(function FarmPondListSearch(props) {
  const { navigation, route } = props;
  const { hidden } = route.params
  const handleHide = () => navigation.setParams({ hidden: !hidden })
  return (
    <View>
      {/* <TouchableOpacity style={TOP_BAR_SEARCH_BUTTON} onPress={handleHide}> */}
      <TouchableOpacity onPress={handleHide}>
        <JalaIcon name="search" size={24} color={color.palette.white}/>
      </TouchableOpacity>
    </View>
  )
})

export const FarmPondListSetting: FC = observer(function FarmPondListSetting(props) {
  return (
    <View style={TOP_BAR_CONTAINER}>
      <TouchableOpacity style={TOP_BAR_RIGHT_BUTTON} onPress={() => navigate('Home.Stack', 'Home.Farm.Edit')}>
        <JalaIcon name="setting" size={24} color={color.palette.white}/>
      </TouchableOpacity>
    </View>
  )
})

const ClosedContent = ({ title, value, unit, colorScheme }: { title?: TxKeyPath, value?:any, unit?: string, colorScheme?: "light" | "dark" }) => (
  <View style={CLOSED_CYCLE_CONTENT_ITEM}>
    <Text preset="body1" tx={title} colorScheme={colorScheme} />
    <View style={CLOSED_CYCLE_CONTENT}>
      <Text preset="title2" text={value} colorScheme={colorScheme} />
      {unit && <Text preset="title1" tx={`cycleHistoryScreen.${_.camelCase(unit)}` as TxKeyPath} colorScheme={colorScheme} marginLeft="1.5" />}
    </View>
  </View>
)

const FarmPicker: FC<{ colorScheme?: "light"|"dark" }> = memo(observer(function FarmPicker(props) {
  const { colorScheme = Appearance.getColorScheme() } = props
  const {
    appStore, appStore: { farmId, farmStore }
  } = useStores()

  const { initiating, ponds, farm } = farmStore || FarmStoreModel.create({})
  const { name } = farm || {}

  const onFarmChange = useCallback(async (value) => {
    await appStore.checkNetworkConnection().catch()
    appStore.set('farmId', value || null)
    await delay(1)
    if (appStore.farmStore) appStore.farmStore.initWithLastBatchAndWaterQualityMultiMeasurements()
  }, [appStore])

  const onPress = useCallback(() => {
    navigate('Home.Stack', 'Home.Pond.PreCreateMultiple')
  }, [])

  return (<View
    backgroundColor={colorScheme === "light" ? color.palette.white : nativeBaseTheme.colors.dark["50"]}
    borderBottomRadius="16px"
    zIndex={1}
    shadow={4}
  >
    <FormControl
      colorScheme={colorScheme}
      labelTx="homeScreen.farm"
      paddingX="16px"
      marginBottom="8px"
    >
      <ModelPicker
        key={`${farmId || ''}-${name || ''}`}
        colorScheme={colorScheme}
        actionSheetColorScheme={colorScheme}
        placeholderTx="homeScreen.noFarm"
        Model={Farm}
        value={farmId}
        onValueChange={onFarmChange}
        isDisabled={initiating}
        InputRightElement={initiating && <Spinner mr="9px"></Spinner>}
        titleTx="homeScreen.label"
        isUseSearch
      />
    </FormControl>
    <View flexDirection="row" justifyContent="space-between" px="16px" mb="10px">
      <Text preset="title4" text={translate("farmPondListScreen.total", { pond: ponds.length })} colorScheme={colorScheme} />
      {!!ponds.length && (
        <Button
          testID="jala:id/add-pond-button"
          colorScheme={colorScheme}
          preset="link"
          tx="pondScreen.addPond"
          textPreset="title4"
          textColor={color.primary}
          onPress={onPress}
        />
      )}
    </View>
  </View>)
}))

export const InitialCycleSyncDialog: FC<{ colorScheme?: "light"|"dark" }> = memo(observer(function InitialCycleSyncDialog(props) {
  const { colorScheme } = props
  const { appStore: { farmStore } } = useStores()
  const { pondStore } = farmStore || FarmStoreModel.create({});
  const { cycleStore } = pondStore || PondStoreModel.create({});
  const { syncingLabel, synced, initiating, progress } = cycleStore || CycleStoreModel.create({});

  useEffect(() => {
    if (cycleStore) cycleStore.set('initiating', false);
  }, [cycleStore])

  return (!synced && <ProgressDialog
    isOpen={initiating}
    colorScheme={colorScheme}
    title={translate("farmPondListScreen.initialDataLoading")}
    subtitle={`${_.startCase(translate(`${syncingLabel}.singular` as TxKeyPath))}...`}
    progressValue={progress}
  />)
}))

export const FarmPondListScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function FarmPondListScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params } } = props
  const { data: { text = '' } = {} } = params || {}
  const {
    appStore, appStore: { farmStore, isOffline },
    farmPondStore, farmPondStore: { navigating },
    farmStore: farmStoreModel, farmStore: { needRefresh: needRefreshFarm },
    pondStore, pondStore: { deleting: deletingPond, needRefresh: needRefreshPond, ponds: pondsRFID },
    cycleStore, cycleStore: { needRefresh: needRefreshCycle },
    waterQualityMultiMeasurementStore,
    waterQualityMultiMeasurementStore: { needRefresh },
    userStore, userStore: { user }
  } = useStores()
  const {
    pondStoreModels,
    queryingPond,
    loadingCycle,
    loadingWaterQualityMultiMeasurement,
    syncedWaterQualityMultiMeasurement
  } = farmStore || FarmStoreModel.create({});

  const [ isVisible, setVisible ] = useState<boolean>(false);
  const [ order, setOrder ] = useState<number>(null);
  const isFocused = useIsFocused();

  useFocusEffect(useCallback(() => {
    appStore.checkNetworkConnection().catch()
  }, [appStore]))

  const BGTooltip = useMemo<any>(() => {
    const data = user.state && !user.state.add_create_pond_coachmark_shown;
    return data;
  }, [user.state])

  const CycleTooltip = useMemo<any>(() => {
    return user.state.add_create_pond_coachmark_shown && !user.state.add_create_pond_coachmark_shown;
  }, [user.state])

  const ponds = useMemo(() => pondStoreModels.filter((pondStore) => {
    return pondStore.pond.name.includes(text)
  }).map((pondStore) => {
    const rfids = ((pondsRFID.find(p => p.id === pondStore.pondId) || {}).rfids || [])
    const cycle = pondStore.lastCycleStore && {
      ...pondStore.lastCycleStore.cycle,
      multi_measurements: pondStore.lastCycleStore.waterQualityMultiMeasurements,
      cycleStore: pondStore.lastCycleStore
    }
    return ({
      ...pondStore.pond,
      rfids,
      last_cycle: cycle,
      pondStore,

      loadingCycle,
      loadingWaterQualityMultiMeasurement,
      syncedWaterQualityMultiMeasurement
    })
  }).sort(((itemA, itemB) => {
    if (!itemA.last_cycle && !itemB.last_cycle) return 0
    if (!itemA.last_cycle && itemB.last_cycle) return 1
    if (itemA.last_cycle.finished_at === null && (itemB.last_cycle && itemB.last_cycle.finished_at != null)) return -1
    if (itemA.last_cycle && !itemB.last_cycle) return -1

    return 0
  })), [loadingCycle, loadingWaterQualityMultiMeasurement, pondStoreModels, pondsRFID, syncedWaterQualityMultiMeasurement, text])
  const isEmpty = !pondStoreModels.length

  useEffect(() => {
    if (user.state && !user.state.add_create_pond_coachmark_shown) {
      setVisible(true);
      setOrder(1);
    }
    if (user.state.add_create_pond_coachmark_shown && !user.state.add_create_cycle_coachmark_shown) {
      setVisible(true);
      setOrder(2);
    }
  }, [user.state])

  // Handle infinite loading
  useEffect(() => {
    if (farmStoreModel.needRefresh) farmStoreModel.set('needRefresh', false);
    if (pondStore.needRefresh) pondStore.set('needRefresh', false);
    if (cycleStore.needRefresh) cycleStore.set('needRefresh', false);
  }, [cycleStore, farmStoreModel, pondStore])

  useEffect(() => {
    if (!farmStore) goBack('Home.Stack')
    farmPondStore.init()
    farmStore.getWaterQualityMultiMeasurements(!isOffline).catch();
  }, [farmPondStore, farmStore, isOffline])

  useEffect(() => {
    if (needRefresh) {
      if (isOffline) {
        farmStore.getWaterQualityMultiMeasurements(false).then(() => {
          waterQualityMultiMeasurementStore.set('needRefresh', null)
          // waterQualityMultiMeasurementStore.set('pushIdQueue', {})
        })
      } else {
        farmStore.getWaterQualityMultiMeasurements(true, () => {
          waterQualityMultiMeasurementStore.set('needRefresh', null)
          // waterQualityMultiMeasurementStore.set('pushIdQueue', {})
        }).catch()
      }
    }
  }, [farmStore, isOffline, needRefresh, waterQualityMultiMeasurementStore])

  const onNavigate = useCallback((selected) => async () => {
    if (farmPondStore.navigating || deletingPond) return;
    farmPondStore.set('navigating', true)
    await delay(0);
    appStore.farmStore.set('pondId', selected.id)
    if (appStore.farmStore.pondStore && appStore.farmStore.pondStore.cycleStoreModels.length) {
      appStore.farmStore.pondStore.set('cycleId', selected.last_cycle.id);
      // if (appStore.farmStore.pondStore.cycleStore.synced) {
        await delay(0);
        navigate('Home.Stack', "Home.Cycle.Stack")
        farmPondStore.set('navigating', false)
      // } else {
      //   farmPondStore.set('navigating', false)
      //   await delay(0);
      //   appStore.farmStore.pondStore.cycleStore.initAllSequential()
      //     .then(() => navigate('Home.Stack', "Home.Cycle.Stack"))
      // }
    }
    // if (farmPondStore.navigateDisabled) {
    //   Toast.show({ title: translate('Please Wait') });
    //   do {
    //     await delay(10)
    //   } while (farmPondStore.navigateDisabled)
    // }
  }, [appStore.farmStore, deletingPond, farmPondStore])

  const onSettingPress = useCallback((selected) => () => {
    // navigate('Home.Stack', "Home.Farm.Pond.Setting", { data: selected });
    navigate('Home.Stack', "Home.Farm.Pond.Setting", { ...selected });
  }, [])

  const onCreatePond = useCallback(() => {
    navigate('Home.Stack', "Home.Pond.PreCreateMultiple")
  }, [])

  const handlePress = useCallback((data) => () => {
    farmStore.set('pondId', data.id)
    navigate('Home.Stack', "Home.Cycle.History");
  }, [farmStore])

  const onCreateCycle = useCallback((data) => () => {
    // pondStore.set('selectedPond', data)
    navigate('Home.Stack', 'Home.Cycle.CreateMultiple', { models: [{
      pond_id: data.id,
      area: data.area,
      deep: data.deep
    }] })
  }, [])

  const handleTooltip = (value) => {
    // scrollViewRef.current.scrollTo({ ...coords, animated: true });
    setOrder(value);
  }

  const onCloseTooltip = useCallback((value) => {
    const params = {
      state: { ...(user.state || {}), [value]: true },
      _method: 'PUT'
    };
    userStore.updateProfile(params).then(() => {
      userStore.set('user', { ...user, state: params.state });
      userStore.syncUser().catch();
    }).catch()
    setVisible(!isVisible)
  }, [isVisible, user, userStore])

  const renderPondItemContentPlaceholder = useCallback(() => {
    return <View style={POND_ITEM_SKELETON} height="212px">
      <Skeleton.Text lines={8}/>
    </View>
  }, [])

  const PondItemContent = useCallback((props) => {
    const { isEmpty, data, index } = props;
    const multiMeasurement: any = !isEmpty && _.first((data.last_cycle || {}).multi_measurements) || {};
    const lastMeasuredAt = !isEmpty && multiMeasurement.measured_at
      ? _.upperFirst(translate('farmPondListScreen.lastUpdate', { datetime: moment(multiMeasurement.measured_at).format('DD MMM YYYY (HH:mm)') }))
      : _.upperFirst(translate('farmPondListScreen.noUpdate'));

    return (
      <View style={POND_ITEM_CONTENT}>
        {isEmpty ? <EmptyContent index={index} onPress={onCreateCycle(data)} colorScheme={colorScheme} /> : (
          <LazyRender renderPlaceholder={renderPondItemContentPlaceholder}>
            <TouchableOpacity onPress={onNavigate(data)} disabled={isEmpty}>
              <View height="212px">
                <Text preset="body3" text={lastMeasuredAt} style={POND_UPDATE_TEXT} colorScheme={colorScheme} color={!multiMeasurement.measured_at ? nativeBaseTheme.colors.danger["500"] : undefined} />
                <ItemContent data={data} colorScheme={colorScheme} />
              </View>
            </TouchableOpacity>
          </LazyRender>
        )}
      </View>
    )
  }, [colorScheme, onCreateCycle, onNavigate, renderPondItemContentPlaceholder])

  const ClosedCycle = useCallback((props) => {
    const { data } = props;
    const name = data?.item.name;
    const isEmpty = !data?.item.last_cycle;
    const finishedDate = data?.item.last_cycle ? data?.item.last_cycle.finished_at : null;
    const formatedDate = finishedDate ? moment(finishedDate).format('DD MMM YYYY') : '-';
    const pro = !isEmpty ? data.item.last_cycle.pro !== null : false;
    const doc = data?.item.last_cycle ? data?.item.last_cycle.age : '-';
    const cycle = data?.item.last_cycle || {};
    return (
      <LazyRender loading={!data}>
        <View style={CLOSED_CYCLE}>
          <PondItemHeader data={data?.item} cycle={cycle} line={true} name={name} pro={pro} colorScheme={colorScheme} onPress={onSettingPress(data?.item)} />
          <View style={CLOSED_CYCLE_CONTENT}>
            <ClosedContent title="pondScreen.ClosedAt" value={formatedDate} colorScheme={colorScheme} />
            <ClosedContent title="pondScreen.age" value={doc} unit="days" colorScheme={colorScheme} />
          </View>
          <View style={CLOSED_CYCLE_BTN_WRAPPER}>
            <Button testID="jala:id/start-cycle-button" tx="pondScreen.startCycle" colorScheme={colorScheme} style={FLEX} onPress={onCreateCycle(data.item)} />
            <Button testID="jala:id/cycle-history-button" preset="outline" tx="pondScreen.cycleHistory" colorScheme={colorScheme} style={FLEX} onPress={handlePress(data.item)} />
          </View>
        </View>
      </LazyRender>
    )
  }, [colorScheme, handlePress, onCreateCycle, onSettingPress])

  const renderPondItemPlaceHolder: ListRenderItem<any> = useCallback((item) => {
    const isEmpty = !item.item.last_cycle;
    const isClosed = !isEmpty && !!item.item.last_cycle.finished_at;

    return (isClosed ? <ClosedCycle data={item} /> : <View style={POND_ITEM_CONTAINER}>
      <View style={POND_ITEM(isEmpty)}>
        <View style={POND_HEADER(true)}>
          <Skeleton height="24px" />
        </View>
        <View style={POND_HEADER(false)}>
          <Skeleton height="16px" />
        </View>
        {!isEmpty && <View style={POND_HEADER(false)}>
          <Skeleton height="16px" />
        </View>}
      </View>
      <View style={POND_ITEM_CONTENT}>
        {isEmpty ? <View style={EMPTY_POND}>
          <Skeleton height="76px" borderRadius="4px" marginBottom="8px"/>
          <Skeleton height="40px" borderRadius="4px" marginY="8px" />
        </View> : <View style={POND_ITEM_SKELETON}>
          <Skeleton.Text lines={8}/>
        </View>}
      </View>
    </View>)
  }, [ClosedCycle])

  const PondItemTooltip = useCallback((props) => {
    const {
      index, cycle, name,
      pro, isEmpty, data,
      onPress, colorScheme
    } = props;
    const isTooltip = index === 0 && isEmpty;;
    return (
        <>
        {isTooltip ? (
          <Tooltip
            isVisible={isFocused && isVisible && order === 2}
            content={<TooltipContent
              title="farmPondListScreen.createCycle"
              index={1}
              total={1}
              onClose={onCloseTooltip}
              onNextPress={handleTooltip}
              coachmark='add_create_cycle_coachmark_shown'
              id="pond-list-create-cycle"
            />}
            placement="bottom"
            onClose={onCloseTooltip}
            contentStyle={POND_TOOLTIP_CONTENT_STYLE}
            useInteractionManager={true} // need this prop to wait for react navigation
            // topAdjustment={Platform.OS === 'android' ? adjustmentHeight : 0}
          >
            <View style={CONTAINER_ITEM}>
              <View style={POND_ITEM_CONTAINER}>
                {CycleTooltip && (
                  <View style={NUMBER_WRAPPER(false)}>
                    <Text preset="body4" mt="2px" text="1" color={color.palette.white} colorScheme={colorScheme} />
                  </View>
                )}
                <PondItemHeader data={data} line={isEmpty} cycle={cycle} name={name} pro={pro} colorScheme={colorScheme} onPress={onPress} />
                <PondItemContent isEmpty={isEmpty} data={data} index={index}/>
              </View>
            </View>
          </Tooltip>
        ) :(
          <View style={POND_ITEM_CONTAINER}>
            <PondItemHeader data={data} line={isEmpty} cycle={cycle} name={name} pro={pro} colorScheme={colorScheme} onPressName={onNavigate(data)} onPress={onPress} />
            <PondItemContent isEmpty={isEmpty} data={data} index={index}/>
          </View>
        )}
        </>
    )
  }, [isFocused, isVisible, order, onCloseTooltip, CycleTooltip, PondItemContent, onNavigate])

  const PondItem: ListRenderItem<{ name, last_cycle, pondStore: PondStore }> = useCallback((item) => {
    // const { onPress } = props;
    const { last_cycle, name, pondStore } = item.item;
    const isEmpty = !last_cycle;
    const isClosed = !isEmpty && !!item.item.last_cycle.finished_at;
    const pro = !isEmpty ? last_cycle.pro !== null : false;
    const loading = isAlive(pondStore) ? !pondStore.syncedCycle : true;
    return (
      <LazyRender loading={loading} renderPlaceholder={renderPondItemPlaceHolder} placeholderProps={item}>
        {/* {isClosed ? <ClosedCycle data={item} /> : (
          <View style={POND_ITEM_CONTAINER}>
            <PondItemHeader line={isEmpty} cycle={last_cycle} name={name} pro={pro} colorScheme={colorScheme} onPress={onSettingPress(item.item)} />
            <PondItemContent index={item.index} isEmpty={isEmpty} data={item.item}/>
          </View>
        )} */}
        {isClosed ? <ClosedCycle data={item} /> : (
          <PondItemTooltip
            index={item.index}
            cycle={last_cycle}
            name={name}
            pro={pro}
            isEmpty={isEmpty}
            data={item.item}
            onPress={onSettingPress(item.item)}
            colorScheme={colorScheme}
          />
        )}
      </LazyRender>
    )
  }, [ClosedCycle, PondItemTooltip, colorScheme, onSettingPress, renderPondItemPlaceHolder])

  return (<>
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundColor={colorScheme === "light" ? color.background : undefined}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideOnBlurred={false}
    >
      <OfflineModeIndicator />
      <FarmPicker colorScheme={colorScheme} />
      <LazyRender>
        {isEmpty || !ponds.length ? (
          <View style={IMG_CONTAINER}>
            {queryingPond ? <ActivityIndicator /> : <>
              <AutoImage source={emptyPond} resizeMode="contain" />
              {isEmpty && <Text preset="title3" tx="pondScreen.emptyPond" style={EMPTY_POND_TEXT} colorScheme={colorScheme} />}
            </>}
          </View>
        ): (
          <FlatList
            data={ponds}
            showsVerticalScrollIndicator={false}
            keyExtractor={(item) => `${appStore.farmId}-${item.id}`}
            renderItem={PondItem}
            contentContainerStyle={FLAT_LIST_CONTAINER}
            ListFooterComponent={<SafeAreaView edges={['bottom']}/>}
          />
        )}
      </LazyRender>
      {isEmpty && !queryingPond && (
        <Tooltip
          isVisible={isVisible && order === 1}
          contentStyle={POND_TOOLTIP_CONTENT_STYLE}
          content={<TooltipContent
            title="farmPondListScreen.pondTooltip"
            index={1}
            total={1}
            onClose={onCloseTooltip}
            onNextPress={handleTooltip}
            coachmark='add_create_pond_coachmark_shown'
            id="pond-list-create-pond"
          />}
          placement="top"
          onClose={() => onCloseTooltip('add_create_pond_coachmark_shown')}
          useInteractionManager={true} // need this prop to wait for react navigation
        >
          <View style={BUTTON_WRAPPER(BGTooltip)}>
            {BGTooltip && (
              <View style={NUMBER_WRAPPER(true)}>
                <Text preset="body4" mt="2px" text="1" color={color.palette.white} colorScheme={colorScheme} />
              </View>
            )}
            <Button
              testID="jala:id/add-pond-button"
              tx="pondScreen.createPond"
              style={BUTTON}
              onPress={onCreatePond}
              colorScheme={colorScheme}
            />
          </View>
        </Tooltip>
      )}
      {isFocused && <Modal isOpen={navigating || needRefreshFarm || needRefreshPond || needRefreshCycle}>
        {/* <Spinner color={color.palette.white} /> */}
        <View
          width="72px" height="72px"
          alignItems="center"
          justifyContent="center"
          rounded="48px"
          backgroundColor="rgba(255, 255, 255, 0.75)"
        >
          <JalaLoading />
        </View>
        {/* <Text tx="Please Wait" colorScheme="dark" /> */}
      </Modal>}
    </Screen>
    <InitialCycleSyncDialog colorScheme={colorScheme} />
    <OfflineMode colorScheme={colorScheme} />
  </>)
})
